import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { Button } from "./ui/button";
import { Coins, Menu, X, Globe } from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { t, lang, toggleLang } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const isActive = (p) => location.pathname === p;

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const navLink = (to, label, testid) => (
    <Link
      to={to}
      data-testid={testid}
      onClick={() => setOpen(false)}
      className={`text-sm uppercase tracking-[0.15em] transition-colors ${
        isActive(to) ? "text-primary" : "text-foreground/70 hover:text-foreground"
      }`}
    >
      {label}
    </Link>
  );

  const LangToggle = ({ testid }) => (
    <button
      onClick={toggleLang}
      data-testid={testid}
      className="flex items-center gap-1.5 border border-white/15 px-2.5 py-1.5 font-mono text-xs uppercase tracking-widest text-foreground/70 transition-colors hover:border-white/40 hover:text-foreground"
    >
      <Globe className="h-3.5 w-3.5" /> {lang === "nl" ? "EN" : "NL"}
    </button>
  );

  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-[#0A0A0A]/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-10">
        <Link to="/" data-testid="nav-logo" className="group flex items-baseline gap-2">
          <span className="font-heading text-2xl font-black tracking-tighter">
            Probably<span className="text-primary">True</span>
          </span>
          <span className="hidden font-mono text-[10px] uppercase tracking-widest text-muted-foreground sm:inline">
            /dossier
          </span>
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          {navLink("/wiki", t("nav.wiki"), "nav-wiki")}
          {navLink("/hall-of-fame", t("nav.hof"), "nav-hof")}
          {navLink("/hall-of-shame", t("nav.hos"), "nav-hos")}
          {user && user.role === "admin" && navLink("/admin", t("nav.editorial"), "nav-admin")}
          {user && user.role === "admin" && navLink("/admin/users", t("nav.users"), "nav-users")}
          {user && user !== false && navLink("/my-bets", t("nav.myBets"), "nav-bets")}
          {user && user !== false && navLink("/settings", t("nav.settings"), "nav-settings")}

          <LangToggle testid="nav-lang-toggle" />

          {user && user !== false ? (
            <div className="flex items-center gap-4">
              <Link
                to="/credits"
                data-testid="nav-credits"
                className="flex items-center gap-2 border border-credit/30 bg-credit/10 px-3 py-1.5 text-sm font-semibold text-credit transition-colors hover:bg-credit/20"
              >
                <Coins className="h-4 w-4" />
                {user.role === "admin" ? "∞" : user.credits}
              </Link>
              <button
                onClick={handleLogout}
                data-testid="nav-logout"
                className="text-sm uppercase tracking-[0.15em] text-foreground/70 transition-colors hover:text-primary"
              >
                {t("nav.logout")}
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <Link to="/login" data-testid="nav-login" className="text-sm uppercase tracking-[0.15em] text-foreground/70 hover:text-foreground">
                {t("nav.login")}
              </Link>
              <Button asChild data-testid="nav-register" className="rounded-md bg-primary font-semibold uppercase tracking-wider hover:bg-[#FF645A]">
                <Link to="/register">{t("nav.register")}</Link>
              </Button>
            </div>
          )}
        </nav>

        <div className="flex items-center gap-3 md:hidden">
          <LangToggle testid="nav-lang-toggle-m" />
          <button onClick={() => setOpen(!open)} data-testid="nav-menu-toggle">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-white/10 bg-[#0A0A0A] px-6 py-6 md:hidden">
          <div className="flex flex-col gap-5">
            {navLink("/wiki", t("nav.wiki"), "nav-wiki-m")}
            {navLink("/hall-of-fame", t("nav.hof"), "nav-hof-m")}
            {navLink("/hall-of-shame", t("nav.hos"), "nav-hos-m")}
            {user && user.role === "admin" && navLink("/admin", t("nav.editorial"), "nav-admin-m")}
            {user && user.role === "admin" && navLink("/admin/users", t("nav.users"), "nav-users-m")}
            {user && user !== false && navLink("/my-bets", t("nav.myBets"), "nav-bets-m")}
            {user && user !== false && navLink("/settings", t("nav.settings"), "nav-settings-m")}
            {user && user !== false ? (
              <>
                {navLink("/credits", `${t("common.credits")}: ${user.role === "admin" ? "∞" : user.credits}`, "nav-credits-m")}
                <button onClick={handleLogout} className="text-left text-sm uppercase tracking-[0.15em] text-primary">
                  {t("nav.logout")}
                </button>
              </>
            ) : (
              <>
                {navLink("/login", t("nav.login"), "nav-login-m")}
                {navLink("/register", t("nav.register"), "nav-register-m")}
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
